/**
 * AndesAccessTab - Dataset detail tab for Andes permissions and subscriptions
 * @module components/starnexus/datasets/tabs/AndesAccessTab
 */

import React, { useState, useEffect, useCallback } from 'react';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Table from '@amzn/awsui-components-console/table';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import Badge from '@amzn/awsui-components-console/badge';
import Alert from '@amzn/awsui-components-console/alert';
import Link from '@amzn/awsui-components-console/link';
import type { PermissionRequest, Subscription, CreatePermissionRequestInput, CreateSubscriptionInput } from '../../../../types/starnexus/andesAccess';
import { PERMISSION_STATUS_INDICATOR, SUBSCRIPTION_STATUS_INDICATOR, TARGET_TYPE_BADGE_COLORS, IDENTITY_TYPE_HELPERS } from '../../../../types/starnexus/andesAccess';
import { andesAccessService } from '../../../../services/starnexus';
import { RequestAccessModal } from '../../andes/RequestAccessModal';
import { CreateSubscriptionModal } from '../../andes/CreateSubscriptionModal';

interface AndesAccessTabProps {
  datasetId: string;
  hasAndesConfig: boolean;
  onNavigateToConfig?: () => void;
}

const formatDate = (date: Date): string => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export const AndesAccessTab: React.FC<AndesAccessTabProps> = ({ datasetId, hasAndesConfig, onNavigateToConfig }) => {
  // Data state
  const [permissions, setPermissions] = useState<PermissionRequest[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);

  // Modal state
  const [requestAccessModalVisible, setRequestAccessModalVisible] = useState(false);
  const [createSubscriptionModalVisible, setCreateSubscriptionModalVisible] = useState(false);

  // Notification state
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Fetch permissions for this dataset
  const fetchPermissions = useCallback(async () => {
    setLoadingPermissions(true);
    try {
      const result = await andesAccessService.getPermissionRequests({
        datasetId
      });
      setPermissions(result);
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
    } finally {
      setLoadingPermissions(false);
    }
  }, [datasetId]);

  // Fetch subscriptions for this dataset
  const fetchSubscriptions = useCallback(async () => {
    setLoadingSubscriptions(true);
    try {
      const result = await andesAccessService.getSubscriptions({
        datasetId
      });
      setSubscriptions(result);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
    } finally {
      setLoadingSubscriptions(false);
    }
  }, [datasetId]);

  // Initial data fetch
  useEffect(() => {
    if (hasAndesConfig) {
      fetchPermissions();
      fetchSubscriptions();
    }
  }, [hasAndesConfig, fetchPermissions, fetchSubscriptions]);

  // Handle permission request submission
  const handleRequestAccessSubmit = async (input: CreatePermissionRequestInput) => {
    await andesAccessService.createPermissionRequest(input);
    setNotification({
      type: 'success',
      message: `Successfully created ${input.datasetIds.length} permission request(s)`
    });
    fetchPermissions();
  };

  // Handle subscription creation
  const handleCreateSubscriptionSubmit = async (input: CreateSubscriptionInput) => {
    await andesAccessService.createSubscription(input);
    setNotification({
      type: 'success',
      message: `Successfully created ${input.datasetIds.length} subscription(s)`
    });
    fetchSubscriptions();
  };

  // If no Andes config, show message
  if (!hasAndesConfig) {
    return (
      <Container header={<Header variant="h2">Andes Access</Header>}>
        <Box textAlign="center" padding="s">
          <SpaceBetween size="s">
            <Box variant="h3">No Andes Configuration</Box>
            <Box color="text-body-secondary">This dataset does not have Andes settings configured. Configure Andes settings to enable permission requests and subscriptions.</Box>
            {onNavigateToConfig && <Button onClick={onNavigateToConfig}>Go to Configuration</Button>}
          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  // Permission request table columns
  const permissionColumns = [
    {
      id: 'identityType',
      header: 'Identity type',
      cell: (item: PermissionRequest) => IDENTITY_TYPE_HELPERS[item.identityType]?.label || item.identityType,
      width: 140
    },
    {
      id: 'identityValue',
      header: 'Identity value',
      cell: (item: PermissionRequest) => (
        <Box fontSize="body-s" variant="code">
          {item.identityValue.length > 35 ? `${item.identityValue.substring(0, 35)}...` : item.identityValue}
        </Box>
      ),
      width: 220
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: PermissionRequest) => <StatusIndicator type={PERMISSION_STATUS_INDICATOR[item.status]}>{item.status.charAt(0).toUpperCase() + item.status.slice(1)}</StatusIndicator>,
      width: 100
    },
    {
      id: 'requestedAt',
      header: 'Requested',
      cell: (item: PermissionRequest) => formatDate(item.requestedAt),
      width: 110
    }
  ];

  // Subscription table columns
  const subscriptionColumns = [
    {
      id: 'targetName',
      header: 'Target name',
      cell: (item: Subscription) => item.targetName,
      width: 180
    },
    {
      id: 'targetType',
      header: 'Type',
      cell: (item: Subscription) => <Badge color={TARGET_TYPE_BADGE_COLORS[item.targetType]}>{item.targetType}</Badge>,
      width: 100
    },
    {
      id: 'region',
      header: 'Region',
      cell: (item: Subscription) => item.region,
      width: 100
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: Subscription) => <StatusIndicator type={SUBSCRIPTION_STATUS_INDICATOR[item.status]}>{item.status.charAt(0).toUpperCase() + item.status.slice(1)}</StatusIndicator>,
      width: 100
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (item: Subscription) => formatDate(item.createdAt),
      width: 110
    }
  ];

  return (
    <SpaceBetween size="m">
      {notification && (
        <Alert type={notification.type} dismissible onDismiss={() => setNotification(null)}>
          {notification.message}
        </Alert>
      )}

      {/* Permissions Section */}
      <Container
        header={
          <Header
            variant="h2"
            counter={`(${permissions.length})`}
            actions={<Button onClick={() => setRequestAccessModalVisible(true)}>Request access</Button>}
            description="Permission requests for this dataset"
          >
            Permissions
          </Header>
        }
      >
        <Table
          columnDefinitions={permissionColumns}
          items={permissions}
          loading={loadingPermissions}
          loadingText="Loading permissions"
          trackBy="requestId"
          empty={
            <Box textAlign="center" color="inherit" padding="s">
              <SpaceBetween size="xs">
                <b>No permission requests</b>
                <Box variant="p" color="inherit">
                  No permission requests have been created for this dataset.
                </Box>
                <Button variant="link" onClick={() => setRequestAccessModalVisible(true)}>
                  Request access
                </Button>
              </SpaceBetween>
            </Box>
          }
          variant="embedded"
        />
        {permissions.length > 0 && (
          <Box padding={{ top: 's' }}>
            <Link href="/starnexus/andes/permissions">View all permission requests →</Link>
          </Box>
        )}
      </Container>

      {/* Subscriptions Section */}
      <Container
        header={
          <Header
            variant="h2"
            counter={`(${subscriptions.length})`}
            actions={<Button onClick={() => setCreateSubscriptionModalVisible(true)}>Create subscription</Button>}
            description="Subscriptions for this dataset"
          >
            Subscriptions
          </Header>
        }
      >
        <Table
          columnDefinitions={subscriptionColumns}
          items={subscriptions}
          loading={loadingSubscriptions}
          loadingText="Loading subscriptions"
          trackBy="subscriptionId"
          empty={
            <Box textAlign="center" color="inherit" padding="s">
              <SpaceBetween size="xs">
                <b>No subscriptions</b>
                <Box variant="p" color="inherit">
                  No subscriptions have been created for this dataset.
                </Box>
                <Button variant="link" onClick={() => setCreateSubscriptionModalVisible(true)}>
                  Create subscription
                </Button>
              </SpaceBetween>
            </Box>
          }
          variant="embedded"
        />
        {subscriptions.length > 0 && (
          <Box padding={{ top: 's' }}>
            <Link href="/starnexus/andes/subscriptions">View all subscriptions →</Link>
          </Box>
        )}
      </Container>

      {/* Request Access Modal */}
      <RequestAccessModal visible={requestAccessModalVisible} onDismiss={() => setRequestAccessModalVisible(false)} onSubmit={handleRequestAccessSubmit} preselectedDatasetId={datasetId} />

      {/* Create Subscription Modal */}
      <CreateSubscriptionModal
        visible={createSubscriptionModalVisible}
        onDismiss={() => setCreateSubscriptionModalVisible(false)}
        onSubmit={handleCreateSubscriptionSubmit}
        preselectedDatasetId={datasetId}
      />
    </SpaceBetween>
  );
};

export default AndesAccessTab;
